// components/slides/pdfImport.ts
//
// Slides module PDF ingest (docs/plans/slides-module-architecture.md §2C):
// client-side import of a taker PDF into a SlideDeck. pdfjs-dist renders each
// page once at import time to (a) measure the page in PDF points (the deck's
// coordinate system), (b) extract its text for LLM context ("vectorize the
// text"), and (c) capture a PNG snapshot for LLM vision + filmstrip fallback.
// The source PDF itself is uploaded untouched — it stays the immutable vector
// base that /api/slides/export re-loads byte-identically.
//
// CLIENT-ONLY: pdfjs-dist is dynamic-imported inside the function so this
// module stays SSR-safe to import from anywhere.
import { uploadFileToCloud } from '#lib/azure-client';
import type { SlideDeckDTO, SlideDeckDoc, SlideDeckRoundArchive, SlideElement, SlidePageDoc } from '#types/types';
import { useSlidesStore } from './slidesStore';

const MAX_TEXT_CHARS = 8000;
const MAX_SNAPSHOT_EDGE_PX = 2048;
const PAGE_UPLOADS_IN_FLIGHT = 3;
/** Positioned labels kept per page for deterministic anchoring. */
const MAX_TEXTMAP_ITEMS = 150;

/** Geometric TextItem midpoint, not a browser-font-dependent glyph ink centre. */
export function textItemMidpoint(
  item: { transform: number[]; width: number; height: number },
  style: { vertical?: boolean },
  viewport: { convertToViewportPoint: (x: number, y: number) => number[] }
): { x: number; y: number } | null {
  const tr = item.transform;
  if (!Array.isArray(tr) || tr.length < 6 || !tr.slice(0, 6).every(Number.isFinite) || !Number.isFinite(item.width) || !Number.isFinite(item.height)) return null;
  const [a, b, c, d, x, y] = tr as [number, number, number, number, number, number];
  const baseline = Math.hypot(a, b);
  const ascent = Math.hypot(c, d);
  if (!baseline || !ascent || item.width < 0 || item.height < 0) return null;
  const rawX = style.vertical ? x - c / ascent * item.height / 2 + a / baseline * item.width / 2
    : x + a / baseline * item.width / 2 + c / ascent * item.height / 2;
  const rawY = style.vertical ? y - d / ascent * item.height / 2 + b / baseline * item.width / 2
    : y + b / baseline * item.width / 2 + d / ascent * item.height / 2;
  const [vx, vy] = viewport.convertToViewportPoint(rawX, rawY);
  return Number.isFinite(vx) && Number.isFinite(vy) ? { x: Math.round(vx! * 10) / 10, y: Math.round(vy! * 10) / 10 } : null;
}

export interface ImportPdfOptions {
  /** Deck/Project name; defaults to the file name without extension. */
  name?: string;
  /** RDM guideline set to review against ('inline_unit' | 'kiosk' | 'food_unit'). */
  guidelineKey: string;
  /** Storage owner for the uploaded assets (project/user id); defaults to requester. */
  ownerId?: string;
  /** Progress callback: done pages out of total (called after each page). */
  onProgress?: (done: number, total: number) => void;
  /** Which step is running, so a slow import never looks frozen. */
  onStage?: (stage: ImportStage) => void;
}

export type ImportStage = 'reading' | 'pages' | 'uploading' | 'saving';

/**
 * Import a PDF file as a new slide deck: upload source + per-page snapshots,
 * create the Project + SlideDeck via POST /api/slides/deck, ingest the deck
 * into the slides store and open it. Returns the created deck.
 */
/** Shared import core: upload the source PDF + render every page (geometry,
 *  text, snapshot + coordinate-grid snapshot). Used by first-round import
 *  (importPdfAsDeck) and revision import (importPdfAsRevision). */
async function renderPdfFile(
  file: File,
  ownerId: string | null,
  onProgress?: (done: number, total: number) => void,
  onStage?: (stage: ImportStage) => void,
): Promise<{ sourceFileUrl: string; pages: SlidePageDoc[]; total: number }> {
  const pdfjs = await import('pdfjs-dist');
  // Served from public/ (see scripts/copy-pdf-worker.mjs). We deliberately do NOT
  // use `new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url)`: that
  // resolves under Turbopack (next dev) but a bare package specifier is not
  // rewritten by webpack (next build), so it 404s in production and every page
  // renders blank. A stable public path works under both bundlers.
  pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

  const importId = crypto.randomUUID();

  onStage?.('reading');
  // Upload the untouched source PDF (the immutable vector base for export) WHILE the pages render:
  // it used to run first, so a large file sat on "Reading the document" with no progress at all.
  let uploaded = false;
  const upload = uploadFileToCloud(file, `slides/${importId}-source.pdf`, file.type || 'application/pdf', undefined, ownerId)
    .finally(() => { uploaded = true; });
  upload.catch(() => undefined); // awaited below; this only keeps an early failure from going unhandled

  const loadingTask = pdfjs.getDocument({ data: await file.arrayBuffer() });
  const pdf = await loadingTask.promise;
  try {
    const total = pdf.numPages;
    const pages: SlidePageDoc[] = [];
    const inFlight = new Set<Promise<void>>();
    const startPageUpload = (work: () => Promise<void>) => {
      // A failed snapshot leaves the page without one, exactly like a failed render did.
      const task: Promise<void> = work()
        .catch((error) => console.error('[Slides][Import] Snapshot upload failed:', error))
        .finally(() => { inFlight.delete(task); });
      inFlight.add(task);
    };
    onStage?.('pages');
    onProgress?.(0, total);

    for (let i = 0; i < total; i++) {
      const page = await pdf.getPage(i + 1);

      // Page geometry in PDF points (scale 1) — the deck coordinate system.
      const viewport = page.getViewport({ scale: 1 });
      const width = viewport.width;
      const height = viewport.height;

      // Text extraction for LLM context (capped — decks can be text-heavy),
      // plus the POSITIONED text map: exact page-point coordinates of every
      // label, the deterministic anchor source for review callouts (the agent
      // names a label; the app resolves the coordinates — no vision guessing).
      let textContent = '';
      let textMap: Array<{ t: string; x: number; y: number }> | undefined;
      try {
        const text = await page.getTextContent();
        textContent = text.items
          .map((item) => ('str' in item ? item.str : ''))
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim()
          .slice(0, MAX_TEXT_CHARS);
        const entries: Array<{ t: string; x: number; y: number }> = [];
        for (const item of text.items) {
          if (!('str' in item)) continue;
          const t = item.str.replace(/\s+/g, ' ').trim();
          if (t.length < 2) continue;
          const centre = textItemMidpoint(item, text.styles[item.fontName] ?? {}, viewport);
          if (!centre) continue;
          entries.push({ t: t.slice(0, 60), ...centre });
          if (entries.length >= MAX_TEXTMAP_ITEMS) break;
        }
        if (entries.length > 0) textMap = entries;
      } catch (error) {
        console.error(`[Slides][Import] Text extraction failed on page ${i + 1}:`, error);
      }

      // Snapshot for LLM vision — scale 2, capped so the long edge <= 2048px.
      const pageDoc: SlidePageDoc = {
        id: crypto.randomUUID(),
        index: i,
        kind: 'pdf',
        pdfPageIndex: i,
        width,
        height,
        textContent: textContent || undefined,
        textMap,
        elements: [],
      };
      pages.push(pageDoc);
      try {
        const scale = Math.min(2, MAX_SNAPSHOT_EDGE_PX / Math.max(width, height));
        const renderViewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(renderViewport.width);
        canvas.height = Math.ceil(renderViewport.height);
        const canvasContext = canvas.getContext('2d');
        if (canvasContext) {
          canvasContext.fillStyle = '#FFFFFF'; // JPEG has no alpha: paint the paper first
          canvasContext.fillRect(0, 0, canvas.width, canvas.height);
          await page.render({ canvasContext, canvas, viewport: renderViewport }).promise;
          // JPEG, not PNG: a 2048px PNG of a drawing set ran to several MB per page, uploaded one
          // page at a time from the reviewer's connection. 0.92 keeps line work and labels sharp.
          const blob = await new Promise<Blob | null>((resolve) =>
            canvas.toBlob(resolve, 'image/jpeg', 0.92)
          );
          // The upload and the grid run in the background while the next page renders.
          if (blob) startPageUpload(async () => {
            const snapshotUrl = (await uploadFileToCloud(
              blob,
              `slides/${importId}-p${i + 1}.jpg`,
              'image/jpeg',
              undefined,
              ownerId
            )) ?? undefined;
            pageDoc.snapshotUrl = snapshotUrl;
            // Generate the coordinate-gridded copy the Reviewer agent anchors off
            // (server-side; see /api/slides/grid-snapshot). Best-effort — on any
            // failure the page ships without a grid and the agent falls back to the
            // clean snapshot + the page's point dimensions.
            if (snapshotUrl) {
              try {
                const gridRes = await fetch('/api/slides/grid-snapshot', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ assetPath: snapshotUrl, width, height }),
                });
                if (gridRes.ok) {
                  const gridJson = (await gridRes.json()) as { gridUrl?: string };
                  if (typeof gridJson.gridUrl === 'string') pageDoc.gridSnapshotUrl = gridJson.gridUrl;
                }
              } catch {
                /* best-effort */
              }
            }
          });
        }
        canvas.width = 0; // release the bitmap eagerly on large decks
        canvas.height = 0;
      } catch (error) {
        console.error(`[Slides][Import] Snapshot failed on page ${i + 1}:`, error);
      }
      page.cleanup();
      onProgress?.(i + 1, total);
      // Bounded: at most PAGE_UPLOADS_IN_FLIGHT snapshots wait in memory and on the wire.
      while (inFlight.size >= PAGE_UPLOADS_IN_FLIGHT) await Promise.race(inFlight);
    }

    if (!uploaded || inFlight.size > 0) onStage?.('uploading');
    await Promise.all(inFlight);
    const sourceFileUrl = await upload;
    if (!sourceFileUrl) throw new Error('Failed to upload the source PDF');
    return { sourceFileUrl, pages, total };
  } finally {
    await loadingTask.destroy().catch(() => undefined);
  }
}

export async function importPdfAsDeck(
  file: File,
  opts: ImportPdfOptions
): Promise<SlideDeckDTO> {
  if (typeof window === 'undefined') {
    throw new Error('importPdfAsDeck is client-only');
  }

  const { sourceFileUrl, pages, total } = await renderPdfFile(file, opts.ownerId ?? null, opts.onProgress, opts.onStage);
  opts.onStage?.('saving');

  const doc: SlideDeckDoc = { pages };
  const name = (opts.name ?? file.name.replace(/\.[^.]+$/, '')).normalize('NFC').trim() || 'Untitled review';

  const res = await fetch('/api/slides/deck', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      guidelineKey: opts.guidelineKey,
      sourceFileUrl,
      sourceFileName: file.name,
      sourcePageCount: total,
      doc,
    }),
  });
  if (!res.ok) {
    const message = await res.text().catch(() => '');
    throw new Error(`Failed to create deck (${res.status}): ${message}`);
  }
  const data = (await res.json()) as { deck?: SlideDeckDTO };
  if (!data.deck) {
    throw new Error('Deck creation returned no deck');
  }

  const store = useSlidesStore.getState();
  store.ingestDeck(data.deck);
  store.setActiveProject(data.deck.projectId);
  return data.deck;
}

/**
 * Import a RESUBMITTED proposal into an EXISTING deck as the next review round.
 * Non-destructive: the current round's review state, page labels and callout
 * texts are archived into doc.rounds (SlideDeckRoundArchive) before the pages
 * are replaced — so the Reviewer can diff V(n) against V(n-1): resolved vs
 * persisting findings, and pieces that disappeared from the resubmission.
 */
export async function importPdfAsRevision(
  file: File,
  projectId: string,
  opts?: { ownerId?: string; onProgress?: (done: number, total: number) => void }
): Promise<SlideDeckDTO> {
  if (typeof window === 'undefined') {
    throw new Error('importPdfAsRevision is client-only');
  }
  const store = useSlidesStore.getState();
  const deck = store.decks[projectId];
  if (!deck) throw new Error('Deck not loaded. Open it before importing a revision.');

  const { sourceFileUrl, pages, total } = await renderPdfFile(file, opts?.ownerId ?? null, opts?.onProgress);

  // Archive the closing round (verdicts + labels + localized findings).
  const archive: SlideDeckRoundArchive = {
    round: deck.reviewRound,
    closedAt: new Date().toISOString(),
    sourceFileName: deck.sourceFileName ?? null,
    pageLabels: deck.doc.pages.map((p) => ({ index: p.index, label: p.label })),
    review: deck.doc.review,
    findings: deck.doc.pages.flatMap((p) =>
      p.elements
        .filter((el): el is Extract<SlideElement, { type: 'note' }> => el.type === 'note' && !!el.text)
        .map((el) => ({ pageIndex: p.index, text: el.text, severity: el.severity, guidelinePointId: el.guidelinePointId }))
    ),
  };

  const nextRound = deck.reviewRound + 1;
  const doc: SlideDeckDoc = {
    ...deck.doc,
    pages,
    // Fresh review for the new round; keep the guideline + the stamp placement.
    review: {
      guidelineKey: deck.guidelineKey,
      round: nextRound,
      points: {},
      header: { ...deck.doc.review?.header, date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit', timeZone: 'UTC' }), issue: undefined },
      ...(deck.doc.review?.stamp ? { stamp: deck.doc.review.stamp } : {}),
    },
    rounds: [...(deck.doc.rounds ?? []), archive],
  };
  delete doc.reviewRun;

  const res = await fetch('/api/slides/deck', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      projectId,
      doc,
      version: deck.version + 1,
      guidelineKey: deck.guidelineKey,
      status: 'REVIEWING',
      reviewRound: nextRound,
      sourceFileUrl,
      sourceFileName: file.name,
      sourcePageCount: total,
    }),
  });
  if (!res.ok) {
    const message = await res.text().catch(() => '');
    throw new Error(`Failed to import revision (${res.status}): ${message}`);
  }
  const data = (await res.json()) as { deck?: SlideDeckDTO };
  if (!data.deck) throw new Error('Revision import returned no deck');

  store.ingestDeck(data.deck);
  // The old round's page ids are gone — re-aim the runtime at the new page 1
  // (ingestDeck preserves existing runtime, which would point at removed pages).
  store.patchRuntime(projectId, {
    activePageId: data.deck.doc.pages[0]?.id,
    selectedElementIds: [],
  });
  store.setActiveProject(data.deck.projectId);
  return data.deck;
}
